import { IconSvg } from 'Components/shared/Icon/index.generated';
import React, { useLayoutEffect, useRef, useState } from 'react';
import { Tooltip, TooltipContent, TooltipTrigger } from '~/lib/ui/tooltip';
import { cn } from '~/lib/utils';
import { DotSeparatedLabelsProps } from './index.types';

/**
 * Displays labels separated by a dot icon.
 * If there's an overflow, the labels are displayed in a column instead.
 */
export const DotSeparatedLabels = ({ labels }: DotSeparatedLabelsProps) => {
  const [isOverflowing, setIsOverflowing] = useState(false);
  const [labelOverflows, setLabelOverflows] = useState<boolean[]>([]);
  const container = useRef<HTMLDivElement>(null);
  const labelRefs = useRef<(HTMLSpanElement | null)[]>([]);

  useLayoutEffect(() => {
    if (isOverflowing) {
      return;
    }

    const el = container.current;
    if (el) {
      setIsOverflowing(el.scrollWidth > el.clientWidth);
    }
  }, [labels, isOverflowing]);

  useLayoutEffect(() => {
    if (!isOverflowing) {
      return;
    }

    const overflows = labelRefs.current.map((spanEl) => {
      if (spanEl) {
        return spanEl.scrollWidth > spanEl.clientWidth;
      }
      return false;
    });

    setLabelOverflows(overflows);
  }, [labels, isOverflowing]);

  const filteredLabels = labels.filter((label) => label);

  return (
    <div
      className={cn('flex items-center justify-center gap-2 mt-1.5', {
        'flex-col': isOverflowing,
      })}
      ref={container}
    >
      {filteredLabels.map((label, index) => {
        const spanElement = (
          <span
            ref={(el) => (labelRefs.current[index] = el)}
            className={cn(
              'max-w-full whitespace-nowrap inline-block text-ellipsis',
              {
                'overflow-hidden': isOverflowing,
              }
            )}
          >
            {label}
          </span>
        );

        return (
          <React.Fragment key={index}>
            {index > 0 && (
              <span className="size-1 shrink-0">{IconSvg['busy-filled']}</span>
            )}
            {labelOverflows[index] ? (
              <Tooltip>
                <TooltipTrigger asChild>{spanElement}</TooltipTrigger>
                <TooltipContent>
                  <p>{label}</p>
                </TooltipContent>
              </Tooltip>
            ) : (
              spanElement
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
};
